/**
 * Tipi di dominio per i privilegi RBAC per ruolo (Admin — configurazione portale).
 *
 * Specchiano le definizioni del product-service:
 *   GET /api/products/admin/role-permissions
 *   PUT /api/products/admin/role-permissions
 *
 * I privilegi (TcPermission) sono TC-centrici: governano l'autorizzazione
 * server-side dei servizi tenaciousc-*. NON vengono propagati a Dependency-Track.
 */

import type { TcPermission } from '../../auth/types.js';

/**
 * Catalogo fisso di tutti i privilegi RBAC assegnabili (specchiato da
 * @tenaciousc/auth → ALL_TC_PERMISSIONS). L'ordine determina le righe della matrice.
 */
export const ALL_TC_PERMISSIONS: readonly TcPermission[] = [
  'VIEW_PORTFOLIO',
  'PORTFOLIO_MANAGEMENT',
  'VIEW_VULNERABILITY',
  'VULNERABILITY_ANALYSIS',
  'VULNERABILITY_MANAGEMENT',
  'VIEW_POLICY_VIOLATION',
  'POLICY_VIOLATION_ANALYSIS',
  'POLICY_MANAGEMENT',
  'BOM_UPLOAD',
  'ACCESS_MANAGEMENT',
  'TAG_MANAGEMENT',
  'SYSTEM_CONFIGURATION',
  'VIEW_AUDIT_LOG',
] as const;

/** Etichette italiane dei privilegi per la visualizzazione nella matrice. */
export const TC_PERMISSION_LABELS: Record<TcPermission, string> = {
  VIEW_PORTFOLIO: 'Visualizza portfolio',
  PORTFOLIO_MANAGEMENT: 'Gestione portfolio',
  VIEW_VULNERABILITY: 'Visualizza vulnerabilità',
  VULNERABILITY_ANALYSIS: 'Analisi vulnerabilità',
  VULNERABILITY_MANAGEMENT: 'Gestione vulnerabilità',
  VIEW_POLICY_VIOLATION: 'Visualizza violazioni policy',
  POLICY_VIOLATION_ANALYSIS: 'Analisi violazioni policy',
  POLICY_MANAGEMENT: 'Gestione policy',
  BOM_UPLOAD: 'Caricamento BOM',
  ACCESS_MANAGEMENT: 'Gestione accessi',
  TAG_MANAGEMENT: 'Gestione tag',
  SYSTEM_CONFIGURATION: 'Configurazione sistema',
  VIEW_AUDIT_LOG: 'Visualizza log audit',
};

/** Mappa ruolo → privilegi RBAC assegnati. Chiave stringa per supportare ruoli custom. */
export type RolePermissionsMap = Record<string, TcPermission[]>;

/**
 * Catalogo dei privilegi assegnabili (Task 6), da `GET /api/products/admin/permissions`:
 * permessi canonici `Resource.Operation` (usati per le righe raggruppate per risorsa nella
 * matrice) + i nomi legacy DT ancora riconosciuti (mostrati in un gruppo collassato separato).
 */
export interface PermissionsCatalog {
  canonical: string[];
  legacy: string[];
}

/** Un gruppo di permessi canonici che condividono la stessa risorsa (`Resource.Operation`). */
export interface CanonicalPermissionGroup {
  resource: string;
  permissions: string[];
}

/**
 * Raggruppa i permessi canonici per risorsa (parte prima del `.`), preservando l'ordine del
 * catalogo. Usato per renderizzare le righe della matrice RBAC raggruppate per risorsa.
 */
export function groupCanonicalByResource(canonical: string[]): CanonicalPermissionGroup[] {
  const groups = new Map<string, string[]>();
  for (const perm of canonical) {
    const resource = perm.split('.')[0] ?? perm;
    const bucket = groups.get(resource);
    if (bucket) bucket.push(perm);
    else groups.set(resource, [perm]);
  }
  return [...groups.entries()].map(([resource, permissions]) => ({ resource, permissions }));
}

/** Estrae l'etichetta leggibile (operazione) di un permesso canonico `Resource.Operation`. */
export function canonicalOperationLabel(permission: string): string {
  return permission.split('.')[1] ?? permission;
}
